import { useCallback, useMemo } from 'react';
import type { ComboboxOption } from '@library/components/selects';
import type { ConfirmationModalProps } from '@library/components/modals/ConfirmationModal';
import { GameModesCombobox } from '@library/components/selects/combobox/GameModesCombobox';
import type { GameModesComboboxProps } from '@library/components/selects/combobox/GameModesCombobox';
import type { TradingPlayerBulkActions } from '@bo-hooks/trading/useTradingPlayerBulkActions';
import type { TradingTeamBulkActions } from '@bo-hooks/trading/useTradingTeamBulkActions';
import { useGameModes } from '@bo-hooks/gameModes/useGameModes';

export interface TradingModalBulkActionProps {
  durationDisplay?: string;
  gameModeIds?: Array<number>;
  isBulkGameModesEqual: boolean;
  setConfirmationModalProps: (
    props: Omit<ConfirmationModalProps, 'onCancelModal'>
  ) => void;
  tradingBulkActions: TradingPlayerBulkActions | TradingTeamBulkActions;
  updateConfirmationModalProp: (
    updateFn: (
      prevProps: Omit<ConfirmationModalProps, 'onCancelModal'>
    ) => Omit<ConfirmationModalProps, 'onCancelModal'>
  ) => void;
}

export const useTradingModalBulkActions = ({
  durationDisplay = '',
  gameModeIds = [],
  isBulkGameModesEqual = false,
  setConfirmationModalProps,
  tradingBulkActions,
  updateConfirmationModalProp
}: TradingModalBulkActionProps) => {
  const { gameModeComboboxOptions: gameModeOptions } = useGameModes({
    isFullDuration: durationDisplay === 'Full'
  });

  const gameModeValues = useMemo(
    () =>
      isBulkGameModesEqual
        ? gameModeOptions.filter(({ value }) =>
            gameModeIds?.includes(Number(value))
          )
        : gameModeOptions,
    [isBulkGameModesEqual, gameModeIds, gameModeOptions]
  );

  const handleBulkGameModes = useCallback(
    async (gameModes: Array<ComboboxOption> | null) => {
      const selectedGameModes = gameModes?.length
        ? gameModes.map(({ value }) => Number(value))
        : gameModeOptions.map(({ value }) => Number(value));
      await tradingBulkActions.handleChangeGameModes(selectedGameModes);
    },
    [gameModeOptions, tradingBulkActions]
  );

  const handleGameModeChange = useCallback(
    (gameModes: Array<ComboboxOption> | null) => {
      updateConfirmationModalProp(
        (prevProps: Omit<ConfirmationModalProps, 'onCancelModal'>) => {
          const gameModesElement =
            prevProps.children as React.ReactElement<GameModesComboboxProps>;
          const gameModeProps = gameModesElement.props;

          return {
            ...prevProps,
            children: (
              <GameModesCombobox
                id={gameModeProps.id}
                onChange={gameModeProps.onChange}
                options={gameModeProps.options}
                value={gameModes}
              />
            ),
            handleConfirm: () => handleBulkGameModes(gameModes),
            isConfirmDisabled: !gameModes?.length
          };
        }
      );
    },
    [handleBulkGameModes, updateConfirmationModalProp]
  );

  const gameModeComboboxProps = useMemo(
    () => ({
      id: 'PlayerGameModes',
      onChange: handleGameModeChange,
      options: gameModeOptions,
      value: gameModeValues
    }),
    [gameModeOptions, gameModeValues, handleGameModeChange]
  );

  const tradingModalActions = useMemo(() => {
    return {
      handleShowGameModes: () => {
        setConfirmationModalProps({
          confirmationCopy: 'Change Game Modes',
          handleConfirm: () => handleBulkGameModes(null),
          label: 'Changing Game Modes',
          children: (
            <GameModesCombobox
              id={gameModeComboboxProps.id}
              onChange={gameModeComboboxProps.onChange}
              options={gameModeComboboxProps.options}
              value={gameModeComboboxProps.value}
            />
          ),
          isOpen: true
        });
      }
    };
  }, [gameModeComboboxProps, handleBulkGameModes, setConfirmationModalProps]);

  return {
    tradingModalActions
  };
};
